import { mkdtemp, mkdir, writeFile, readFile, rm } from "node:fs/promises";
import { tmpdir } from "node:os";
import { dirname, join } from "node:path";
import { GitClient } from "./git/GitClient.js";

/**
 * Apply a patch's `patch_content` to a base content string, returning the
 * post-apply (THEIRS) content.
 *
 * Free-function counterpart to `ReplayApplicator.applyPatchToContent`,
 * usable from `ReplayService.migratePatchesToSnapshot` without the
 * applicator's instance state. Creates its own temporary git repo each
 * call — fine for the one-shot migration path; not a hot loop.
 *
 * Returns `null` when the patch can't apply against the base (line
 * anchors don't match, malformed diff, or git apply rejects). Callers
 * fall back to a different THEIRS source.
 */
export async function applyPatchToContent(
    base: string,
    patchContent: string,
    filePath: string,
): Promise<string | null> {
    const fileDiff = extractFileDiff(patchContent, filePath);
    if (!fileDiff) return null;

    const tempDir = await mkdtemp(join(tmpdir(), "replay-migrate-"));
    const tempGit = new GitClient(tempDir);
    try {
        await tempGit.exec(["init"]);
        await tempGit.exec(["config", "user.email", "migrate@fern.com"]);
        await tempGit.exec(["config", "user.name", "Fern Replay Migration"]);
        await tempGit.exec(["config", "commit.gpgSign", "false"]);

        const tempFilePath = join(tempDir, filePath);
        await mkdir(dirname(tempFilePath), { recursive: true });
        await writeFile(tempFilePath, base);

        await tempGit.exec(["add", filePath]);
        await tempGit.exec(["commit", "-m", `base for ${filePath}`, "--allow-empty"]);

        await tempGit.execWithInput(["apply", "--allow-empty"], fileDiff);

        return await readFile(tempFilePath, "utf-8");
    } catch {
        return null;
    } finally {
        await rm(tempDir, { recursive: true, force: true }).catch(() => {});
    }
}

/**
 * Extract the diff hunks for `filePath` from a multi-file unified-diff
 * patch. Returns the slice starting at the file's `diff --git` header
 * up to the next `diff --git` (or end of input). Mirrors the logic in
 * `ReplayApplicator.extractFileDiff` — kept here for reuse without
 * exposing the private method.
 */
function extractFileDiff(patchContent: string, filePath: string): string | null {
    const lines = patchContent.split("\n");
    const out: string[] = [];
    let inTarget = false;
    for (const line of lines) {
        if (line.startsWith("diff --git")) {
            if (inTarget) break;
            const match = line.match(/^diff --git a\/.+ b\/(.+)$/);
            if (match && match[1] === filePath) {
                inTarget = true;
                out.push(line);
            }
            continue;
        }
        if (inTarget) out.push(line);
    }
    return out.length > 0 ? out.join("\n") + "\n" : null;
}
